import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Row, Col, Form } from 'reactstrap';
import ReactButton from 'components/Buttons/ReactButton';
import ReactLoader from 'components/Loader';
import { FlexControllerV2 } from 'components/FlexControllerInput/V2';
import useQueues from 'hooks/useQueues';
import useMonitoringQueueGroups from 'hooks/useMonitoringQueueGroups';
import UserService from 'views/User/service';

const MonitoringQueueGroupForm = ({
  groupData,
  onSuccess,
  onCancel,
  readOnly = false
}) => {
  const { createGroup, updateGroup, getGroupById } = useMonitoringQueueGroups();
  const [usersOptions, setUsersOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const { options: queueOptions, isLoading: loadingQueues } = useQueues({
    showDisabled: false,
    types: ['is_chat', 'is_telephony']
  });

  useEffect(() => {
    setLoadingUsers(true);
    UserService.getAllSeachUser(1, 1000, '', false, '', 'name', 'ASC')
      .then((response) => {
        const users = response.data.data.map((u) => ({
          label: u.name || u.login,
          value: u.login
        }));
        setUsersOptions(users);
      })
      .catch((err) => {
        console.error('Erro ao carregar usuários', err);
      })
      .finally(() => {
        setLoadingUsers(false);
      });
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      name: '',
      queues: [],
      agents: [],
      accessAgentsLogin: []
    }
  });

  useEffect(() => {
    async function loadGroupDetails() {
      if (groupData) {
        setLoadingDetails(true);
        try {
          const fullGroupData = getGroupById
            ? await getGroupById(groupData.id)
            : null;
          const dataToUse = fullGroupData || groupData;

          const formattedQueues = dataToUse.queues
            ? dataToUse.queues.map((q) => ({
                label: q.name || q.queue_name || `Fila ${q.id || q}`,
                value: q.id || q
              }))
            : [];

          const formattedAgents = dataToUse.agents
            ? dataToUse.agents.map((a) => ({
                label: a.name || a.login,
                value: a.login
              }))
            : [];

          const formattedAccessAgents = dataToUse.accessAgentsLogin
            ? dataToUse.accessAgentsLogin.map((a) => ({
                label: a.name || a.login,
                value: a.login
              }))
            : [];

          reset({
            name: dataToUse.name,
            queues: formattedQueues,
            agents: formattedAgents,
            accessAgentsLogin: formattedAccessAgents
          });
        } catch (error) {
          console.error('Erro ao carregar detalhes do grupo', error);
        } finally {
          setLoadingDetails(false);
        }
      } else {
        reset({ name: '', queues: [], agents: [], accessAgentsLogin: [] });
      }
    }

    loadGroupDetails();
  }, [groupData, reset, getGroupById]);

  const onSubmit = async (data) => {
    const payload = {
      name: data.name,
      queues: data.queues ? data.queues.map((q) => q.value) : []
    };

    let success = false;

    if (groupData?.id) {
      success = await updateGroup(groupData.id, payload);
    } else {
      success = await createGroup(payload);
    }

    if (success) {
      onSuccess();
    }
  };

  if (loadingQueues || loadingUsers || loadingDetails) {
    return (
      <Row className="justify-content-center py-4">
        <ReactLoader />
      </Row>
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col md="12">
          <FlexControllerV2
            type="text"
            name="name"
            control={control}
            label="Nome do Agrupamento"
            placeholder="Ex: Suporte Nível 1"
            rules={{
              required: 'O nome é obrigatório',
              minLength: {
                value: 3,
                message: 'O nome deve ter no mínimo 3 caracteres'
              }
            }}
            errors={errors}
            disabled={readOnly}
          />
        </Col>

        <Col md="12">
          <FlexControllerV2
            type="select"
            name="queues"
            control={control}
            label="Filas"
            placeholder="Selecione as filas..."
            options={queueOptions}
            isMulti
            required={!readOnly}
            rules={{
              required: 'Selecione pelo menos uma fila'
            }}
            errors={errors}
            isDisabled={readOnly}
          />
        </Col>

        <Col md="12" className="mt-2">
          <FlexControllerV2
            type="select"
            name="agents"
            control={control}
            label="Agentes"
            placeholder="Selecione os agentes..."
            options={usersOptions}
            isMulti
            required={!readOnly}
            rules={{
              required: 'Selecione pelo menos um agente'
            }}
            errors={errors}
            isDisabled={readOnly}
          />
        </Col>

        {!readOnly && (
          <Col md="12" className="mt-2">
            <FlexControllerV2
              type="select"
              name="accessAgentsLogin"
              control={control}
              label="Permissão de Visualização"
              placeholder="Selecione os usuários..."
              options={usersOptions}
              isMulti
              required={!readOnly}
              rules={{
                required: 'Selecione pelo menos um usuário'
              }}
              errors={errors}
              isDisabled={readOnly}
            />
          </Col>
        )}
      </Row>

      <div className="d-flex justify-content-end mt-4">
        <ReactButton
          btnColor="cancelation"
          onClick={onCancel}
          type="button"
          className="mr-2"
          disabled={isSubmitting}
        >
          {readOnly ? 'Voltar' : 'Cancelar'}
        </ReactButton>
        {!readOnly && (
          <ReactButton
            btnColor="confirmation"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </ReactButton>
        )}
      </div>
    </Form>
  );
};

export default MonitoringQueueGroupForm;
